import { useAction } from "convex/react";
import { useCallback, useEffect, useState } from "react";

import { api } from "@convex/_generated/api";
import type { NotebookFrontmatter } from "@domain/notebook-frontmatter";
import {
  notebookDeleteFailed,
  notebookInvalidPath,
  notebookLoadFailed,
  notebookNoteNotFound,
  notebookNoteTooLarge,
  notebookReadFailed,
  notebookSaveFailed,
} from "@domain/user-errors";
import { errorCodeText } from "@/lib/convexErrors";
import { logMutationError } from "@/lib/logger";

/**
 * A single note's list-view metadata (spec §4.2 `listNotes` row). Content is
 * NOT carried here — the list is a metadata-only surface to avoid N GETs; the
 * body is fetched lazily via `readNote`. Distinct from the domain `NoteMeta`
 * (which is a derived display shape); this mirrors the action's list item.
 */
export interface NoteListItem {
  path: string;
  sizeBytes: number;
  updatedAt: string | null;
}

/** A fully-read note (body + parsed frontmatter). Frontmatter is advisory and
 *  may be `null` when absent or unparseable (lenient domain parse). */
export interface Note {
  path: string;
  content: string;
  frontmatter: NotebookFrontmatter | null;
}

export interface UseNotebook {
  notes: NoteListItem[];
  isLoading: boolean;
  /** User-facing copy for the most recent list-load failure, or null. */
  error: string | null;
  /** Timestamp (ms) of the last successful list load, or null before the first. */
  lastRefreshedAt: number | null;
  refresh: () => Promise<void>;
  readNote: (args: { path: string }) => Promise<Note>;
  saveNote: (args: {
    path: string;
    content: string;
    /** Provenance source on rename-as-move — the note's OLD path, so
     *  `createdBy`/`createdAt` survive the move (server merge-preserve). */
    fromPath?: string;
  }) => Promise<{ path: string }>;
  deleteNote: (args: { path: string }) => Promise<void>;
}

// ─── Coded-error → copy mapping ──────────────────────────────────────
// The code is read from `ConvexError.data`, NEVER `error.message`
// (frontend-doctrine §900, BINDING v3.60.0). A plain `Error` message is
// REDACTED on a production Convex deployment — it reaches the browser as
// `[Request ID: …] Server Error` — so a `.message.includes(CODE)` match
// succeeds in dev and silently never fires in prod, collapsing every coded
// branch to the operation's generic copy. `errorCodeText` reads `.data`
// first and falls back to the message, so plain-`Error` throw sites still
// map correctly. A transport (502-class) throw matches no code and falls
// through to the generic copy by design. Swept per rnk-scc1, matching
// `useStudioApps`.

function codeTextOf(error: unknown): string {
  return errorCodeText(error);
}

/** Machine-readable failure class, so callers can branch (e.g. NOT_FOUND →
 *  not-found pane) without string-matching display copy. */
export type NotebookErrorCode =
  | "NOT_FOUND"
  | "NOTE_TOO_LARGE"
  | "INVALID_NOTE_PATH"
  | "UNKNOWN";

function codeOf(error: unknown): NotebookErrorCode {
  const code = codeTextOf(error);
  if (code.includes("NOT_FOUND")) return "NOT_FOUND";
  if (code.includes("NOTE_TOO_LARGE")) return "NOTE_TOO_LARGE";
  if (code.includes("INVALID_NOTE_PATH")) return "INVALID_NOTE_PATH";
  return "UNKNOWN";
}

function readErrorCopy(error: unknown): string {
  const code = codeTextOf(error);
  if (code.includes("NOT_FOUND")) return notebookNoteNotFound();
  if (code.includes("NOTE_TOO_LARGE")) return notebookNoteTooLarge();
  if (code.includes("INVALID_NOTE_PATH")) return notebookInvalidPath();
  return notebookReadFailed();
}

function saveErrorCopy(error: unknown): string {
  const code = codeTextOf(error);
  if (code.includes("NOTE_TOO_LARGE")) return notebookNoteTooLarge();
  if (code.includes("INVALID_NOTE_PATH")) return notebookInvalidPath();
  return notebookSaveFailed();
}

/** Thrown by `readNote`/`saveNote`/`deleteNote` so callers can surface copy
 *  inline without re-deriving it. `.userMessage` is the display string. */
export class NotebookError extends Error {
  readonly userMessage: string;
  readonly code: NotebookErrorCode;
  readonly reason: unknown;
  constructor(userMessage: string, code: NotebookErrorCode, reason: unknown) {
    super(userMessage);
    this.name = "NotebookError";
    this.userMessage = userMessage;
    this.code = code;
    this.reason = reason;
  }
}

/**
 * The single notebook action surface (spec §4.3). Fetch-on-mount + manual
 * `refresh`; NO auto-retry, NO intervals (spec §1.2: no client polling). The
 * GCS `notebook/` prefix is the sole source of truth — this hook holds no
 * cache beyond the current list snapshot; the visible "last refreshed"
 * affordance owns the staleness contract (frontend-doctrine §11-inv-4 carve-out).
 */
export function useNotebook(): UseNotebook {
  const listNotesAction = useAction(api.notebookActions.listNotes);
  const readNoteAction = useAction(api.notebookActions.readNote);
  const writeNoteAction = useAction(api.notebookActions.writeNote);
  const deleteNoteAction = useAction(api.notebookActions.deleteNote);

  const [notes, setNotes] = useState<NoteListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listNotesAction({});
      setNotes(result?.notes ?? []);
      setLastRefreshedAt(Date.now());
    } catch (err) {
      logMutationError({ mutation: "notebookActions.listNotes", error: err });
      setError(notebookLoadFailed());
    } finally {
      setIsLoading(false);
    }
  }, [listNotesAction]);

  // Fetch-on-mount. No interval, no retry — refetch is user- or event-driven.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const readNote = useCallback(
    async ({ path }: { path: string }): Promise<Note> => {
      try {
        const result = await readNoteAction({ path });
        return {
          path: result.path,
          content: result.content,
          frontmatter: result.frontmatter,
        };
      } catch (err) {
        logMutationError({ mutation: "notebookActions.readNote", args: { path }, error: err });
        throw new NotebookError(readErrorCopy(err), codeOf(err), err);
      }
    },
    [readNoteAction],
  );

  const saveNote = useCallback(
    async ({
      path,
      content,
      fromPath,
    }: {
      path: string;
      content: string;
      fromPath?: string;
    }): Promise<{ path: string }> => {
      try {
        return await writeNoteAction({ path, content, fromPath });
      } catch (err) {
        logMutationError({ mutation: "notebookActions.writeNote", args: { path }, error: err });
        throw new NotebookError(saveErrorCopy(err), codeOf(err), err);
      }
    },
    [writeNoteAction],
  );

  const deleteNote = useCallback(
    async ({ path }: { path: string }): Promise<void> => {
      try {
        await deleteNoteAction({ path });
      } catch (err) {
        logMutationError({ mutation: "notebookActions.deleteNote", args: { path }, error: err });
        throw new NotebookError(notebookDeleteFailed(), codeOf(err), err);
      }
    },
    [deleteNoteAction],
  );

  return {
    notes,
    isLoading,
    error,
    lastRefreshedAt,
    refresh,
    readNote,
    saveNote,
    deleteNote,
  };
}
